import React, { useState } from 'react';
import {
  Accordion,
  Badge,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import {
  COMPARISON_LABELS,
  REVIEW_DECISION_LABELS,
  REVIEW_ACTION_LABELS,
  EvidenceCitationSchema,
  reviewAreaLabel,
  type ProjectDetail,
  type WorkflowException,
  type ReviewAction,
} from '@tinypro-audit/contracts';
import { z } from 'zod';
import { useRevalidator } from 'react-router-dom';
import { ReviewDecisionEditor } from '../components/ReviewDecisionEditor';
import { fetchJson } from '../api/client';
import {
  fieldLabel,
  openingLabel,
  projectReviewModel,
} from './project-review-model';
import { Evidence } from './ProjectEvidence';
import { formatDateTimeInAppTimeZone } from '@tinypro-audit/shared/time';

export function IssueDecisions({
  tasks,
  data,
  issueScope,
}: {
  tasks: WorkflowException[];
  data: ProjectDetail;
  issueScope?: { orderId: string; title: string };
}) {
  const eligible = tasks.filter(
    (t) => t.latest_run_id === data.project.latest_run_id,
  );
  const sameTopic =
    new Set(eligible.map((t) => `${t.order_id}:${t.field}`)).size === 1;
  const groupedIssue = Boolean(
    issueScope &&
    eligible.length &&
    eligible.every((t) => t.order_id === issueScope.orderId),
  );
  const sameScope = sameTopic || groupedIssue;
  // A topic starts with every current comparison selected. Explicit subsets
  // stay explicit when data refreshes; the editor guards changes to the scope.
  const [ids, setIds] = useState<string[] | null>(sameScope ? null : []);
  const [refs, setRefs] = useState<string[]>([]);
  const selected =
    ids === null ? eligible : eligible.filter((t) => ids.includes(t.id));
  const revalidator = useRevalidator();
  if (!tasks.length)
    return (
      <Text size="sm" c="dimmed">
        No follow-up decisions are needed for this topic.
      </Text>
    );
  const commonDecision =
    selected.length &&
    selected.every(
      (t) =>
        t.review_decision?.action === selected[0].review_decision?.action &&
        t.review_decision?.note === selected[0].review_decision?.note,
    )
      ? selected[0].review_decision
      : null;
  const allSelected = selected.length === eligible.length;
  const openingRefs = new Set(eligible.map((t) => t.opening_ref));
  const selectedOpenings = new Set(selected.map((t) => t.opening_ref));
  const openingScope =
    sameScope && !openingRefs.has(null)
      ? openingRefs.size === 1
        ? ' for this opening'
        : ` across ${selectedOpenings.size} selected ${selectedOpenings.size === 1 ? 'opening' : 'openings'}`
      : '';
  const scope = sameScope
    ? !selected.length
      ? `Select at least one comparison for this ${groupedIssue ? 'issue' : 'topic'}.`
      : `Applies to ${allSelected ? `${selected.length > 1 ? 'all ' : ''}${selected.length}` : `${selected.length} of ${eligible.length}`} ${eligible.length === 1 ? 'comparison' : 'comparisons'}${openingScope}.`
    : `Applies to ${selected.length} selected ${selected.length === 1 ? 'comparison' : 'comparisons'}.`;
  const decisionLabel = (task: WorkflowException) =>
    task.review_status === 'verified_after_recheck'
      ? 'Verified after recheck'
      : task.review_decision
        ? REVIEW_DECISION_LABELS[task.review_decision.action]
        : task.state === 'resolved'
          ? 'Closed by reviewer'
          : 'Needs review';
  const description = (task: WorkflowException) => {
    const order = data.workflow.orders.find((o) => o.id === task.order_id);
    const comparison = String(
      task.content.comparison ?? task.stage.split(':')[1] ?? '',
    );
    return [
      !sameScope
        ? `PO ${order?.identity.po_number ?? order?.order_ref ?? 'Project'} · ${fieldLabel(task.field)}`
        : null,
      groupedIssue ? fieldLabel(task.field) : null,
      task.opening_ref ? openingLabel(data, task.opening_ref) : task.line_ref,
      comparison === 'po_vs_estimate' &&
      task.stage.startsWith('acknowledgement:')
        ? `${COMPARISON_LABELS.po_vs_estimate} (acknowledgement review)`
        : ((COMPARISON_LABELS as Record<string, string>)[comparison] ??
          reviewAreaLabel(task.stage)),
    ]
      .filter(Boolean)
      .join(' · ');
  };
  const taskIds = new Set(tasks.map((t) => t.id));
  const events = data.workflow.events.filter(
    (e) =>
      e.exception_id &&
      taskIds.has(e.exception_id) &&
      [
        'not_issue',
        'needs_correction',
        'waiting',
        'correction_recorded',
        'corrected',
        'reopen',
        'resolve',
        'acknowledge',
        'await_clarification',
        'recheck',
        'recheck_matched',
      ].includes(e.action),
  );
  const comparisons = (
    <Stack gap="xs" mt="sm">
      {sameScope && !allSelected && (
        <Button
          size="xs"
          variant="subtle"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setIds(null)}
        >
          Select all {eligible.length} comparisons
        </Button>
      )}
      {tasks.map((task) => (
        <Paper key={task.id} withBorder p="sm" radius="sm">
          <Group justify="space-between" wrap="wrap">
            <Checkbox
              checked={selected.some((t) => t.id === task.id)}
              disabled={!eligible.some((t) => t.id === task.id)}
              label={description(task)}
              onChange={(e) => {
                const checked = e.currentTarget.checked;
                setIds((ids) =>
                  checked
                    ? [...(ids ?? eligible.map((t) => t.id)), task.id]
                    : (ids ?? eligible.map((t) => t.id)).filter(
                        (id) => id !== task.id,
                      ),
                );
              }}
            />
            <Badge
              size="sm"
              variant="light"
              color={
                task.review_status === 'verified_after_recheck'
                  ? 'teal'
                  : 'gray'
              }
            >
              {decisionLabel(task)}
            </Badge>
          </Group>
          {task.review_decision && (
            <Text size="xs" c="dimmed" mt="xs">
              {task.review_decision.note} ·{' '}
              {task.review_decision.actor_name ?? 'Reviewer'}
            </Text>
          )}
        </Paper>
      ))}
    </Stack>
  );
  return (
    <Stack gap="md">
      <ReviewDecisionEditor
        title={
          groupedIssue
            ? 'Review this issue'
            : sameTopic
              ? `Review ${fieldLabel(eligible[0].field)}`
              : undefined
        }
        decision={commonDecision || null}
        canReopen={selected.some(
          (t) => t.review_decision !== null || t.state === 'resolved',
        )}
        verified={
          !!selected.length &&
          selected.every((t) => t.review_status === 'verified_after_recheck')
        }
        revision={JSON.stringify(
          selected.map((t) => [
            t.id,
            t.updated_at,
            t.last_decision_id,
            t.review_source_version,
          ]),
        )}
        scope={scope}
        scopeDetails={
          sameScope ? (
            <details>
              <summary>Choose individual comparisons</summary>
              {comparisons}
            </details>
          ) : (
            comparisons
          )
        }
        disabledReason={
          !projectReviewModel(data).current
            ? 'Wait for the current evidence to finish review.'
            : !selected.length
              ? 'Select the comparisons this decision applies to.'
              : undefined
        }
        correctionHelp="Confirm that you fixed and checked the selected comparisons. Saving closes them as Resolved by reviewer. The original analysis stays available; a later analysis can flag remaining issues."
        onSave={async (action, note) => {
          await fetchJson(
            `/api/projects/${data.project.st_project_id}/review-decisions`,
            {
              method: 'POST',
              body: JSON.stringify({
                action,
                note,
                source_version: selected[0].review_source_version,
                items: selected.map((t) => ({
                  id: t.id,
                  expected_updated_at: t.updated_at,
                  expected_decision_id: t.last_decision_id,
                })),
                evidence: refs.map((ref) => {
                  const [kind, id] = ref.split(':');
                  return { kind, id };
                }),
              }),
            },
            z.unknown(),
          );
          setRefs([]);
          await revalidator.revalidate();
        }}
      >
        <MultiSelect
          label="Supporting evidence (optional)"
          placeholder="Choose a document or pre-site review"
          searchable
          clearable
          value={refs}
          onChange={setRefs}
          data={[
            ...data.documents.map((d) => ({
              value: `document:${d.id}`,
              label: String(d.metadata.original_filename ?? d.id),
            })),
            ...data.workflow.assessments
              .filter((a) => a.current)
              .map((a) => ({
                value: `measurement_assessment:${a.id}`,
                label: `Pre-site review ${a.source_review_id}`,
              })),
          ]}
        />
      </ReviewDecisionEditor>
      {!!events.length && (
        <Accordion>
          <Accordion.Item value="history">
            <Accordion.Control>
              Decision history · {events.length} records
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                {events.map((e) => (
                  <div key={e.id}>
                    <Text size="sm" fw={500}>
                      {REVIEW_ACTION_LABELS[e.action as ReviewAction] ??
                        (e.action === 'recheck_matched'
                          ? 'Matching recheck recorded'
                          : fieldLabel(e.action))}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatDateTimeInAppTimeZone(e.created_at)} ·{' '}
                      {data.workflow.members.find(
                        (m) => m.user_id === e.actor_user_id,
                      )?.name ?? (e.actor_user_id ? 'Reviewer' : 'Analysis')}
                      {typeof e.details.source_version === 'string' &&
                      e.details.source_version !==
                        tasks[0]?.review_source_version
                        ? ' · Earlier evidence'
                        : e.action === 'corrected' &&
                            e.details.run_id !== data.project.latest_run_id
                          ? ' · Earlier analysis'
                          : ''}
                    </Text>
                    {typeof e.details.note === 'string' && (
                      <Text size="sm">{e.details.note}</Text>
                    )}
                    {Array.isArray(e.details.evidence) && (
                      <Evidence
                        items={e.details.evidence.flatMap((ref) => {
                          if (!ref || typeof ref !== 'object') return [];
                          const citation = EvidenceCitationSchema.safeParse({
                            ...ref,
                            page: ref.page ?? null,
                            quote: ref.quote ?? '',
                          });
                          return citation.success ? [citation.data] : [];
                        })}
                      />
                    )}
                  </div>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
    </Stack>
  );
}
